import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => n==null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s).replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const curYear = () => toEng(new Date().toLocaleDateString('fa-IR').split('/')[0]);
const todayJalali = () => {
  const d=new Date(), gy=d.getFullYear(), gm=d.getMonth()+1, gd=d.getDate();
  const gDM=[0,31,59,90,120,151,181,212,243,273,304,334];
  const gy2=gy-1600,gm2=gm-1,gd2=gd-1;
  let days=365*gy2+Math.floor((gy2+3)/4)-Math.floor((gy2+99)/100)+Math.floor((gy2+399)/400)+gDM[gm2]+gd2;
  if(gm2>1&&((gy%4===0&&gy%100!==0)||gy%400===0))days+=1;
  let jDN=days-79; const jNp=Math.floor(jDN/12053); jDN%=12053;
  let jy=979+33*jNp+4*Math.floor(jDN/1461); jDN%=1461;
  if(jDN>=366){jy+=Math.floor((jDN-1)/365);jDN=(jDN-1)%365;}
  const jDIM=[31,31,31,31,31,31,30,30,30,30,30,29]; let jm=0;
  while(jm<11&&jDN>=jDIM[jm]){jDN-=jDIM[jm];jm++;}
  const p=n=>String(n).padStart(2,'0');
  return `${String(jy).padStart(4,'0')}/${p(jm+1)}/${p(jDN+1)}`;
};

const API = (path, opts={}) =>
  fetch(`/api/income${path}`, {
    headers:{'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('access')}`}, ...opts,
  }).then(jsonOf, netErr);

/* ── فیلدها خارج کامپوننت ── */
const FInput = ({label,name,value,onChange,readOnly,half,type='text'}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:half?'0 0 48%':1}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}} />
  </div>
);
const FSelect = ({label,name,value,onChange,opts,half}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:half?'0 0 48%':1}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value} onChange={onChange}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',background:'#fff'}}>
      {opts.map(o=><option key={o.val} value={o.val}>{o.label}</option>)}
    </select>
  </div>
);

const INCOME_KINDS = [{val:0,label:'عمومی'},{val:1,label:'اختصاصی'}];
const REGIONAL_KINDS = [{val:0,label:'استانی'},{val:1,label:'ملی'},{val:2,label:'متفرقه'}];
const EVAT_KINDS = [{val:0,label:'—'},{val:1,label:'عوارض'},{val:2,label:'آلایندگی'}];

/* ستون‌ها */
const INIT_WIDTHS = [45, 90, 80, 90, 85, 250, 100, 110, 110, 110, 110, 110, 110, 110, 110];
const EMPTY = {code:'',title:'',income_kind:0,regional_income_kind:0,evat_income_kind:0};

export default function IncomeList({ user, onBack }) {
  // دکمه‌های فرم FRMINCOME — دیدن = دسترسی
  const vbf = useFormButtons('FRMINCOME', ["BTNNEW","BTNEDIT","BTNPREDICTION","BTNDELETE","BTNOK"]);
  const [rows,setRows] = useState([]);
  const [total,setTotal] = useState(0);
  const [hasMore,setHasMore] = useState(false);
  const [loading,setLoading] = useState(false);
  const [year,setYear] = useState(curYear);
  const [dateFrom,setDateFrom] = useState(()=>curYear()+'/01/01');
  const [dateTo,setDateTo] = useState(todayJalali);
  const [showAll,setShowAll] = useState(true);
  const [selected,setSelected] = useState(null);
  const [modal,setModal] = useState(null);
  const [form,setForm] = useState(EMPTY);
  const [error,setError] = useState('');
  const [toast,setToast] = useState('');
  const [predModal,setPredModal] = useState(false);
  const [predictions,setPredictions] = useState([]);
  const [predYear,setPredYear] = useState(curYear);
  const [predAmount,setPredAmount] = useState('');
  const offsetRef = useRef(0);

  const {widths,TH} = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    allRows:rows, searchFields:['code','title'], serverTotal:total, serverHasMore:hasMore,
  });

  const fetchData = useCallback(async(reset=false, y=year, df=dateFrom, dt=dateTo)=>{
    if(reset){offsetRef.current=0;setRows([]);}
    setLoading(true);
    try{
      const off=reset?0:offsetRef.current;
      const d=await API(`/incomes/?offset=${off}&year=${y}&from=${df}&to=${dt}&all=${showAll?'1':'0'}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setHasMore(d.has_more);
        offsetRef.current=(reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[year,dateFrom,dateTo,showAll]);

  useEffect(()=>{fetchData(true);},[]);// eslint-disable-line

  const showToast=msg=>{setToast(msg);setTimeout(()=>setToast(''),2500);};
  const handleYearChange=e=>{
    const v=toEng(e.target.value).replace(/\D/g,'').slice(0,4);
    setYear(v);
    if(v.length===4){setDateFrom(v+'/01/01');fetchData(true,v,v+'/01/01',dateTo);}
  };
  const handleChange=e=>{const{name,value}=e.target;setForm(p=>({...p,[name]:value}));};

  const openNew=()=>{ if (!vbf.can('BTNNEW')) return;setForm(EMPTY);setError('');setModal('new');};
  const openEdit=()=>{ if (!vbf.can('BTNEDIT')) return;
    if(!selected)return;
    setForm({code:selected.code,title:selected.title,income_kind:selected.income_kind,
      regional_income_kind:selected.regional_kind,evat_income_kind:selected.evat_kind});
    setError('');setModal('edit');
  };
  const handleSave=async()=>{
    if(!form.code.trim()||!form.title.trim()){setError('کد و عنوان الزامی است');return;}
    setError('');
    const payload={...form,income_kind:+form.income_kind,regional_income_kind:+form.regional_income_kind,evat_income_kind:+form.evat_income_kind};
    const url=modal==='new'?'/incomes/create/':`/incomes/${selected.id}/update/`;
    const d=await API(url,{method:'POST',body:JSON.stringify(payload)});
    if(d.ok){setModal(null);fetchData(true);showToast(modal==='new'?'✅ ثبت شد':'✅ ویرایش شد');}
    else setError(d.error||'خطا');
  };
  const handleDelete=async()=>{
    if(!selected)return;
    const d=await API(`/incomes/${selected.id}/delete/`,{method:'POST'});
    if(d.ok){setModal(null);setSelected(null);fetchData(true);showToast('✅ حذف شد');}
    else setError(d.error||'خطا');
  };

  /* پیش‌بینی */
  const openPrediction=async()=>{ if (!vbf.can('BTNPREDICTION')) return;
    if(!selected)return;
    setPredYear(year); setPredAmount('');
    const d=await API(`/incomes/${selected.id}/prediction/?year=${year}`);
    if(d.ok)setPredictions(d.predictions||[]);
    setPredModal(true);
  };
  const savePrediction=async()=>{ if (!vbf.can('BTNPREDICTION')) return;
    if(!predAmount)return;
    const d=await API(`/incomes/${selected.id}/prediction/`,{method:'POST',
      body:JSON.stringify({year:+predYear,amount:+toEng(predAmount)})});
    if(d.ok){showToast('✅ پیش‌بینی ثبت شد');openPrediction();}
    else setError(d.error||'خطا');
  };

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>معرفی درآمدها</div><div style={s.hSub}>بخش درآمد — سامانه ارجان</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>
      <main style={s.main}>
        {/* نوار ابزار */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...s.btnEdit,opacity:selected?1:.4}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={()=>{if(selected){setError('');setModal('delete');}}} disabled={!selected} style={{...s.btnDel,opacity:selected?1:.4}}>✕ حذف</button>}
            {vbf.can('BTNPREDICTION') && <button onClick={openPrediction} disabled={!selected} style={{...s.btnPred,opacity:selected?1:.4}}>📈 پیش بینی درآمد</button>}
          </div>
          <div style={{marginRight:'auto',display:'flex',alignItems:'flex-end',gap:'8px',flexWrap:'wrap'}}>
            <div style={{display:'flex',flexDirection:'column',gap:'2px'}}>
              <div style={s.fL}>سال مالی</div>
              <input value={toFa(year)} onChange={handleYearChange} maxLength={4} style={{...s.fI,width:'70px',textAlign:'right'}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'2px'}}>
              <div style={s.fL}>از تاریخ</div>
              <input value={toFa(dateFrom)} onChange={e=>{const v=toEng(e.target.value);setDateFrom(v);if(v.length===10)fetchData(true,year,v,dateTo);}} maxLength={10} style={{...s.fI,width:'95px',direction:'ltr'}}/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'2px'}}>
              <div style={s.fL}>تا تاریخ</div>
              <input value={toFa(dateTo)} onChange={e=>{const v=toEng(e.target.value);setDateTo(v);if(v.length===10)fetchData(true,year,dateFrom,v);}} maxLength={10} style={{...s.fI,width:'95px',direction:'ltr'}}/>
            </div>
            <label style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'12px',cursor:'pointer',alignSelf:'flex-end',paddingBottom:'6px'}}>
              <input type="checkbox" checked={showAll} onChange={e=>{setShowAll(e.target.checked);fetchData(true);}}/>همه حسابها
            </label>
            <div style={{display:'flex',flexDirection:'column',gap:'2px',minWidth:'150px'}}>
              <div style={s.fL}>جستجو</div>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="کد / عنوان..." style={s.fI}/>
            </div>
          </div>
          <button onClick={()=>fetchData(true)} style={s.btnRefresh}>🔄</button>
          <div style={s.countBadge}>{toFa(visibleRows.length)} از {toFa(totalCount)}</div>
        </div>

        {/* جدول */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1}>عمومی/اختصاصی</TH>
                <TH idx={2}>نوع درآمد</TH>
                <TH idx={3}>نوع توزیع</TH>
                <TH idx={4}>کد</TH>
                <TH idx={5} right>عنوان</TH>
                <TH idx={6}>شماره حساب</TH>
                <TH idx={7} gold>بدهکار(قبل)</TH>
                <TH idx={8} gold>بستانکار(قبل)</TH>
                <TH idx={9} gold>بدهکار(جاری)</TH>
                <TH idx={10} gold>بستانکار(جاری)</TH>
                <TH idx={11} gold>بدهکار(کل)</TH>
                <TH idx={12} gold>بستانکار(کل)</TH>
                <TH idx={13} green>مانده بدهکار</TH>
                <TH idx={14} green>مانده بستانکار</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0&&!loading&&(<tr><td colSpan={15} style={s.empty}>📭 رکوردی یافت نشد</td></tr>)}
              {visibleRows.map((r,i)=>{
                const sel=selected?.id===r.id;
                return(<tr key={r.id} onClick={()=>setSelected(r)} style={{cursor:'pointer',background:sel?'#bbdefb':i%2===0?'#fff':'#fdf6e3'}}
                  onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#e3f2fd';}}
                  onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=i%2===0?'#fff':'#fdf6e3';}}>
                  <td style={s.td}>{toFa(i+1)}</td>
                  <td style={s.td}>{r.income_kind_t}</td>
                  <td style={s.td}>{r.regional_kind_t}</td>
                  <td style={s.td}>{r.evat_kind_t}</td>
                  <td style={s.td}>{r.code}</td>
                  <td style={{...s.td,textAlign:'right'}}>{r.title}</td>
                  <td style={{...s.td,direction:'ltr'}}>{r.account_no}</td>
                  <td style={s.num}>{numFmt(r.debit_before)}</td>
                  <td style={s.num}>{numFmt(r.credit_before)}</td>
                  <td style={s.num}>{numFmt(r.debit_current)}</td>
                  <td style={s.num}>{numFmt(r.credit_current)}</td>
                  <td style={s.num}>{numFmt(r.debit_total)}</td>
                  <td style={s.num}>{numFmt(r.credit_total)}</td>
                  <td style={{...s.num,color:'#1b5e20',fontWeight:'700'}}>{numFmt(r.remain_debit)}</td>
                  <td style={{...s.num,color:'#b71c1c',fontWeight:'700'}}>{numFmt(r.remain_credit)}</td>
                </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>
      </main>

      {/* مودال جدید/ویرایش */}
      {(modal==='new'||modal==='edit')&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}><div style={s.mTitle}>{modal==='new'?'➕ درآمد جدید':'✎ ویرایش درآمد'}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <div style={s.row}>
                <FInput label="کد درآمد" name="code" value={form.code} onChange={handleChange} readOnly={modal==='edit'} half/>
                <FInput label="عنوان" name="title" value={form.title} onChange={handleChange} half/>
              </div>
              <div style={s.row}>
                <FSelect label="عمومی/اختصاصی" name="income_kind" value={form.income_kind} onChange={handleChange} opts={INCOME_KINDS} half/>
                <FSelect label="نوع درآمد (منطقه‌ای)" name="regional_income_kind" value={form.regional_income_kind} onChange={handleChange} opts={REGIONAL_KINDS} half/>
              </div>
              <div style={s.row}>
                <FSelect label="نوع توزیع درآمد" name="evat_income_kind" value={form.evat_income_kind} onChange={handleChange} opts={EVAT_KINDS} half/>
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={handleSave} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* مودال حذف */}
      {modal==='delete'&&selected&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'440px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div><button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <p style={{fontSize:'14px',lineHeight:'2'}}>آیا از حذف درآمد «<b>{selected.title}</b>» با کد <b>{selected.code}</b> مطمئن هستید؟</p>
              <p style={{fontSize:'12px',color:'#888'}}>در صورتی که این درآمد در اسناد حسابداری، وصولی‌ها یا نمونه‌های سمپ استفاده شده باشد، حذف امکان‌پذیر نیست.</p>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>انصراف</button>
              {vbf.can('BTNDELETE') && <button onClick={handleDelete} style={{...s.btnSave,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>حذف</button>}
            </div>
          </div>
        </div>
      )}

      {/* مودال پیش‌بینی درآمد */}
      {predModal&&selected&&(
        <div style={s.overlay} onClick={()=>setPredModal(false)}>
          <div style={{...s.modal,maxWidth:'500px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#1a237e,#283593)'}}>
              <div style={s.mTitle}>📈 پیش بینی درآمد — {selected.title}</div>
              <button onClick={()=>setPredModal(false)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              <div style={s.row}>
                <FInput label="سال" name="predYear" value={predYear} onChange={e=>setPredYear(toEng(e.target.value))} half/>
                <FInput label="مبلغ پیش‌بینی" name="predAmount" value={predAmount} onChange={e=>setPredAmount(e.target.value)} half type="number"/>
              </div>
              {vbf.can('BTNPREDICTION') && <button onClick={savePrediction} style={{...s.btnSave,marginBottom:'14px'}}>ثبت پیش‌بینی</button>}
              {predictions.length>0&&(
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                  <thead><tr style={{background:'#f0f2f7'}}>
                    <th style={{padding:'8px',border:'1px solid #e0e0e0'}}>سال</th>
                    <th style={{padding:'8px',border:'1px solid #e0e0e0'}}>مبلغ پیش‌بینی</th>
                  </tr></thead>
                  <tbody>{predictions.map((p,i)=>(
                    <tr key={i}><td style={{padding:'6px 8px',border:'1px solid #e0e0e0',textAlign:'center'}}>{toFa(p.year)}</td>
                    <td style={{padding:'6px 8px',border:'1px solid #e0e0e0',textAlign:'left',direction:'ltr'}}>{numFmt(p.amount)}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
            <div style={s.mFooter}><button onClick={()=>setPredModal(false)} style={s.btnCancel}>بستن</button></div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minHeight:0},
  toolbar:{display:'flex',alignItems:'flex-end',gap:'10px',background:'#fff',padding:'10px 16px',borderRadius:'12px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',marginBottom:'8px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555'},
  fI:{padding:'7px 11px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right'},
  btnNew:{padding:'8px 18px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:{padding:'8px 18px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDel:{padding:'8px 18px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnPred:{padding:'8px 18px',background:'linear-gradient(135deg,#4a148c,#6a1b9a)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnRefresh:{padding:'8px 14px',background:'linear-gradient(135deg,#37474f,#455a64)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',alignSelf:'flex-end'},
  countBadge:{padding:'6px 12px',background:'#1a2f50',color:'#C8A84B',borderRadius:'20px',fontSize:'12px',fontWeight:'700',whiteSpace:'nowrap',alignSelf:'flex-end'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'12px',boxShadow:'0 2px 12px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:0},
  td:{padding:'7px 9px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{padding:'7px 9px',borderBottom:'1px solid #f0f0f0',fontSize:'12px',whiteSpace:'nowrap',direction:'ltr',textAlign:'left',fontVariantNumeric:'tabular-nums'},
  empty:{textAlign:'center',padding:'48px',color:'#999',fontSize:'14px'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'640px',maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center'},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};